import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, BehaviorSubject, combineLatest, timer } from 'rxjs';
import { 
  map, 
  filter, 
  takeUntil, 
  distinctUntilChanged,
  debounceTime,
  switchMap,
  startWith,
  scan,
  share,
  tap
} from 'rxjs/operators';
import { WebSocketService } from './websocket.service';
import {
  PriceUpdateMessage,
  CompetitorPriceMessage,
  PriceAlertMessage,
  MarketChangeMessage,
  AnalyticsUpdateMessage
} from '../../shared/interfaces/websocket.interfaces';

export interface PriceMonitoringConfig {
  alertThreshold: number; // Percentage change threshold for alerts
  competitorUpdateInterval: number; // Milliseconds
  maxPriceHistory: number; // Number of historical prices to keep
  enablePredictiveAlerts: boolean;
  batchUpdateInterval: number; // Milliseconds for batching updates
}

export interface ProductPriceData {
  productId: string;
  currentPrice: number;
  previousPrice: number;
  changePercent: number;
  priceHistory: PriceHistoryEntry[];
  competitors: CompetitorPrice[];
  lastUpdated: number;
  trend: 'up' | 'down' | 'stable';
  volatility: 'low' | 'medium' | 'high';
}

export interface PriceHistoryEntry {
  price: number;
  timestamp: number;
  source: 'system' | 'competitor' | 'manual';
}

export interface CompetitorPrice {
  competitorId: string;
  competitorName: string;
  price: number;
  previousPrice: number;
  changePercent: number;
  marketplace: string;
  lastUpdated: number;
  isLowestPrice: boolean;
}

export interface PriceAlert {
  id: string;
  productId: string;
  type: 'price_drop' | 'price_increase' | 'competitor_undercut' | 'margin_warning' | 'volatility_spike';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  currentPrice: number;
  targetPrice?: number;
  competitorPrice?: number;
  timestamp: number;
  acknowledged: boolean;
}

export interface MarketMetrics {
  totalProducts: number;
  pricesUpdatedToday: number;
  averagePriceChange: number;
  competitorUpdates: number;
  activeAlerts: number;
  marketTrend: 'bullish' | 'bearish' | 'neutral';
  volatilityIndex: number;
}

@Injectable({
  providedIn: 'root'
})
export class RealTimePriceService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private config: PriceMonitoringConfig;
  
  // State management
  private productPrices$ = new BehaviorSubject<Map<string, ProductPriceData>>(new Map());
  private priceAlerts$ = new BehaviorSubject<PriceAlert[]>([]);
  private marketMetrics$ = new BehaviorSubject<MarketMetrics>({
    totalProducts: 0,
    pricesUpdatedToday: 0,
    averagePriceChange: 0,
    competitorUpdates: 0,
    activeAlerts: 0,
    marketTrend: 'neutral',
    volatilityIndex: 0
  });

  // Monitoring state
  private subscribedProducts = new Set<string>();
  private subscribedCompetitors = new Set<string>();

  constructor(private webSocketService: WebSocketService) {
    this.config = {
      alertThreshold: 5, // 5% change threshold
      competitorUpdateInterval: 60000, // 1 minute
      maxPriceHistory: 100,
      enablePredictiveAlerts: true,
      batchUpdateInterval: 1000 // 1 second
    };

    this.initializeRealTimeStreams();
    this.setupMarketMetricsCalculation();
  }

  private initializeRealTimeStreams(): void {
    // Handle price updates
    this.webSocketService.priceUpdates
      .pipe(takeUntil(this.destroy$))
      .subscribe(updates => this.processPriceUpdates(updates));

    // Handle competitor price updates
    this.webSocketService.competitorPrices
      .pipe(takeUntil(this.destroy$))
      .subscribe(update => this.processCompetitorPriceUpdate(update));

    // Handle price alerts from server
    this.webSocketService.priceAlerts
      .pipe(takeUntil(this.destroy$))
      .subscribe(alert => this.processPriceAlert(alert));

    // Handle market changes
    this.webSocketService.marketChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(change => this.processMarketChange(change));

    // Handle analytics updates
    this.webSocketService.analyticsUpdates
      .pipe(takeUntil(this.destroy$))
      .subscribe(update => this.processAnalyticsUpdate(update));
  }

  private processPriceUpdates(updates: PriceUpdateMessage[]): void {
    const currentPrices = new Map(this.productPrices$.value);
    let hasChanges = false;

    updates.forEach(update => {
      const existing = currentPrices.get(update.productId);
      const priceHistory = existing?.priceHistory || [];
      
      // Add new price to history
      priceHistory.push({
        price: update.currentPrice,
        timestamp: update.timestamp,
        source: update.source
      });

      // Limit history size
      if (priceHistory.length > this.config.maxPriceHistory) {
        priceHistory.shift();
      }

      // Calculate trend and volatility
      const trend = this.calculatePriceTrend(priceHistory);
      const volatility = this.calculateVolatility(priceHistory);

      const updatedProduct: ProductPriceData = {
        productId: update.productId,
        currentPrice: update.currentPrice,
        previousPrice: update.previousPrice,
        changePercent: update.changePercent,
        priceHistory,
        competitors: existing?.competitors || [],
        lastUpdated: update.timestamp,
        trend,
        volatility
      };

      currentPrices.set(update.productId, updatedProduct);
      hasChanges = true;

      // Generate alerts if needed
      this.checkForPriceAlerts(updatedProduct);
    });

    if (hasChanges) {
      this.productPrices$.next(currentPrices);
    }
  }

  private processCompetitorPriceUpdate(update: CompetitorPriceMessage): void {
    const currentPrices = new Map(this.productPrices$.value);
    const existing = currentPrices.get(update.productId);

    if (!existing) return;

    // Update or add competitor price
    const competitors = [...existing.competitors];
    const competitorIndex = competitors.findIndex(c => c.competitorId === update.competitorId);

    const competitorPrice: CompetitorPrice = {
      competitorId: update.competitorId,
      competitorName: update.competitorName,
      price: update.price,
      previousPrice: update.previousPrice,
      changePercent: update.priceChange,
      marketplace: update.marketplace,
      lastUpdated: update.timestamp,
      isLowestPrice: false
    };

    if (competitorIndex >= 0) {
      competitors[competitorIndex] = competitorPrice;
    } else {
      competitors.push(competitorPrice);
    }

    // Determine lowest price
    const sortedCompetitors = [...competitors].sort((a, b) => a.price - b.price);
    competitors.forEach(c => {
      c.isLowestPrice = c.competitorId === sortedCompetitors[0]?.competitorId;
    });

    const updatedProduct: ProductPriceData = {
      ...existing,
      competitors,
      lastUpdated: Math.max(existing.lastUpdated, update.timestamp)
    };

    currentPrices.set(update.productId, updatedProduct);
    this.productPrices$.next(currentPrices);

    // Check for competitor undercut alerts
    this.checkForCompetitorAlerts(updatedProduct, competitorPrice);
  }

  private processPriceAlert(alert: PriceAlertMessage): void {
    const currentAlerts = [...this.priceAlerts$.value];
    
    const newAlert: PriceAlert = {
      id: alert.id,
      productId: alert.productId,
      type: alert.alertType,
      severity: alert.severity,
      message: alert.message,
      currentPrice: alert.currentPrice,
      targetPrice: alert.targetPrice,
      competitorPrice: alert.competitorPrice,
      timestamp: alert.timestamp,
      acknowledged: false
    };

    // Check if alert already exists
    const existingIndex = currentAlerts.findIndex(a => a.id === alert.id);
    if (existingIndex >= 0) {
      currentAlerts[existingIndex] = newAlert;
    } else {
      currentAlerts.push(newAlert);
    }

    // Sort by severity and timestamp
    currentAlerts.sort((a, b) => {
      const severityOrder = { critical: 3, warning: 2, info: 1 };
      const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
      return severityDiff !== 0 ? severityDiff : b.timestamp - a.timestamp;
    });

    this.priceAlerts$.next(currentAlerts);
  }

  private processMarketChange(change: MarketChangeMessage): void {
    // Update market metrics based on market changes
    const currentMetrics = this.marketMetrics$.value;
    
    // Update trend based on change type
    let marketTrend: 'bullish' | 'bearish' | 'neutral' = currentMetrics.marketTrend;
    
    if (change.changeType === 'trend_up') {
      marketTrend = 'bullish';
    } else if (change.changeType === 'trend_down') {
      marketTrend = 'bearish';
    } else if (change.changeType === 'market_shift') {
      marketTrend = 'neutral';
    }

    // Update volatility index
    let volatilityIndex = currentMetrics.volatilityIndex;
    if (change.changeType === 'volatility_high') {
      volatilityIndex = Math.min(volatilityIndex + 10, 100);
    }

    this.marketMetrics$.next({
      ...currentMetrics,
      marketTrend,
      volatilityIndex
    });
  }

  private processAnalyticsUpdate(update: AnalyticsUpdateMessage): void {
    // Update relevant metrics based on analytics data
    const currentMetrics = this.marketMetrics$.value;
    const updatedMetrics = { ...currentMetrics };

    if (update.type === 'performance' && update.data['priceUpdates']) {
      updatedMetrics.pricesUpdatedToday = update.data['priceUpdates'];
    }

    if (update.type === 'competition' && update.data['competitorUpdates']) {
      updatedMetrics.competitorUpdates = update.data['competitorUpdates'];
    }

    this.marketMetrics$.next(updatedMetrics);
  }

  private setupMarketMetricsCalculation(): void {
    // Recalculate market metrics every minute
    timer(0, 60000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => combineLatest([
          this.productPrices$,
          this.priceAlerts$
        ]))
      )
      .subscribe(([products, alerts]) => {
        this.calculateMarketMetrics(products, alerts);
      });
  }

  private calculateMarketMetrics(products: Map<string, ProductPriceData>, alerts: PriceAlert[]): void {
    const productArray = Array.from(products.values());
    const todayStart = new Date().setHours(0, 0, 0, 0);
    
    const totalProducts = productArray.length;
    const pricesUpdatedToday = productArray.filter(p => p.lastUpdated > todayStart).length;
    const averagePriceChange = productArray.reduce((sum, p) => sum + p.changePercent, 0) / totalProducts || 0;
    const competitorUpdates = productArray.reduce((sum, p) => 
      sum + p.competitors.filter(c => c.lastUpdated > todayStart).length, 0
    );
    const activeAlerts = alerts.filter(a => !a.acknowledged).length;
    
    // Calculate volatility index
    const volatilityIndex = productArray.reduce((sum, p) => {
      const volatilityWeight = p.volatility === 'high' ? 3 : p.volatility === 'medium' ? 2 : 1;
      return sum + volatilityWeight;
    }, 0) / totalProducts * 10 || 0;

    this.marketMetrics$.next({
      totalProducts,
      pricesUpdatedToday,
      averagePriceChange,
      competitorUpdates,
      activeAlerts,
      marketTrend: this.marketMetrics$.value.marketTrend, // Keep existing trend
      volatilityIndex: Math.min(volatilityIndex, 100)
    });
  }

  private calculatePriceTrend(history: PriceHistoryEntry[]): 'up' | 'down' | 'stable' {
    if (history.length < 2) return 'stable';
    
    const recent = history.slice(-5); // Last 5 entries
    const avgRecent = recent.reduce((sum, entry) => sum + entry.price, 0) / recent.length;
    const older = history.slice(-10, -5); // Previous 5 entries
    const avgOlder = older.reduce((sum, entry) => sum + entry.price, 0) / older.length || avgRecent;
    
    const changePercent = ((avgRecent - avgOlder) / avgOlder) * 100;
    
    if (changePercent > 2) return 'up';
    if (changePercent < -2) return 'down';
    return 'stable';
  }

  private calculateVolatility(history: PriceHistoryEntry[]): 'low' | 'medium' | 'high' {
    if (history.length < 5) return 'low';
    
    const recent = history.slice(-10);
    const prices = recent.map(entry => entry.price);
    const avg = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    const variance = prices.reduce((sum, price) => sum + Math.pow(price - avg, 2), 0) / prices.length;
    const stdDev = Math.sqrt(variance);
    const coefficientOfVariation = (stdDev / avg) * 100;
    
    if (coefficientOfVariation > 10) return 'high';
    if (coefficientOfVariation > 5) return 'medium';
    return 'low';
  }

  private checkForPriceAlerts(product: ProductPriceData): void {
    const alerts: PriceAlert[] = [];
    
    // Price change alert
    if (Math.abs(product.changePercent) > this.config.alertThreshold) {
      alerts.push({
        id: `price-change-${product.productId}-${Date.now()}`,
        productId: product.productId,
        type: product.changePercent > 0 ? 'price_increase' : 'price_drop',
        severity: Math.abs(product.changePercent) > 15 ? 'critical' : 'warning',
        message: `Price ${product.changePercent > 0 ? 'increased' : 'decreased'} by ${Math.abs(product.changePercent).toFixed(2)}%`,
        currentPrice: product.currentPrice,
        timestamp: Date.now(),
        acknowledged: false
      });
    }

    // Volatility alert
    if (product.volatility === 'high') {
      alerts.push({
        id: `volatility-${product.productId}-${Date.now()}`,
        productId: product.productId,
        type: 'volatility_spike' as any,
        severity: 'warning',
        message: 'High price volatility detected',
        currentPrice: product.currentPrice,
        timestamp: Date.now(),
        acknowledged: false
      });
    }

    // Add alerts to the stream
    if (alerts.length > 0) {
      const currentAlerts = [...this.priceAlerts$.value, ...alerts];
      this.priceAlerts$.next(currentAlerts);
    }
  }

  private checkForCompetitorAlerts(product: ProductPriceData, competitor: CompetitorPrice): void {
    // Competitor undercut alert
    if (competitor.price < product.currentPrice) {
      const undercut = ((product.currentPrice - competitor.price) / product.currentPrice) * 100;
      
      if (undercut > 5) { // 5% undercut threshold
        const alert: PriceAlert = {
          id: `competitor-undercut-${product.productId}-${competitor.competitorId}-${Date.now()}`,
          productId: product.productId,
          type: 'competitor_undercut',
          severity: undercut > 15 ? 'critical' : 'warning',
          message: `${competitor.competitorName} is pricing ${undercut.toFixed(2)}% lower`,
          currentPrice: product.currentPrice,
          competitorPrice: competitor.price,
          timestamp: Date.now(),
          acknowledged: false
        };

        const currentAlerts = [...this.priceAlerts$.value, alert];
        this.priceAlerts$.next(currentAlerts);
      }
    }
  }

  // Public API methods
  subscribeToProduct(productId: string): void {
    if (!this.subscribedProducts.has(productId)) {
      this.subscribedProducts.add(productId);
      this.webSocketService.subscribeToProduct(productId);
    }
  }

  unsubscribeFromProduct(productId: string): void {
    if (this.subscribedProducts.has(productId)) {
      this.subscribedProducts.delete(productId);
      this.webSocketService.unsubscribeFromProduct(productId);
    }
  }

  subscribeToProducts(productIds: string[]): void {
    const newProducts = productIds.filter(id => !this.subscribedProducts.has(id));
    newProducts.forEach(id => this.subscribedProducts.add(id));
    
    if (newProducts.length > 0) {
      this.webSocketService.subscribeToProducts(newProducts);
    }
  }

  getProductPrice(productId: string): Observable<ProductPriceData | undefined> {
    return this.productPrices$.pipe(
      map(prices => prices.get(productId)),
      distinctUntilChanged()
    );
  }

  getAllProductPrices(): Observable<ProductPriceData[]> {
    return this.productPrices$.pipe(
      map(prices => Array.from(prices.values()))
    );
  }

  getPriceAlerts(): Observable<PriceAlert[]> {
    return this.priceAlerts$.asObservable();
  }

  getActiveAlerts(): Observable<PriceAlert[]> {
    return this.priceAlerts$.pipe(
      map(alerts => alerts.filter(alert => !alert.acknowledged))
    );
  }

  getMarketMetrics(): Observable<MarketMetrics> {
    return this.marketMetrics$.asObservable();
  }

  acknowledgeAlert(alertId: string): void {
    const currentAlerts = this.priceAlerts$.value.map(alert =>
      alert.id === alertId ? { ...alert, acknowledged: true } : alert
    );
    this.priceAlerts$.next(currentAlerts);
  }

  clearAcknowledgedAlerts(): void {
    const activeAlerts = this.priceAlerts$.value.filter(alert => !alert.acknowledged);
    this.priceAlerts$.next(activeAlerts);
  }

  updateConfig(config: Partial<PriceMonitoringConfig>): void {
    this.config = { ...this.config, ...config };
  }

  getConfig(): PriceMonitoringConfig {
    return { ...this.config };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Unsubscribe from all products
    this.subscribedProducts.forEach(productId => {
      this.webSocketService.unsubscribeFromProduct(productId);
    });
  }
}